import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import { capitalize } from "lodash";
import * as _ from "lodash";

import { PaginatedParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import LikeToDo from "src/library/components/LikeToDo/LikeToDo";
import { ProductCard } from "src/core/products/components/ProductCard";
import { ImportModal } from "src/core/shared/components/ImportModal";
import { breakpoint } from "src/library/utils/styled-media-queries/index";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  useProductsApi,
  IProduct,
  ListProductsFilters
} from "src/core/products/api/products.api";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import { ProductExportModal } from "src/core/products/components/ProductExportModal";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { Text } from "src/library/components/Text";
import { CheckboxStyled } from "src/library/components/Forms";
import { useNutritionApi } from "src/modules/nutrition/api/nutrition.api";
import {
  ERPOdoo,
  ERPUnity
} from "src/utils/constants";
import { ConnectorProductImportModal } from "src/modules/connector/components/ConnectorProductImportModal";
import { useConnectorApi } from "src/modules/connector/api/connector.api";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import { getProductTableColumns } from "src/core/shared/components/tables/productTableColumns";
import { Table } from "src/core/dashboard/components/widgets";
import { ITableInstance } from "src/library/components/Tables/Table";
import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  CurrentView,
  GridViewButton
} from "src/library/components/GridViewButton";

const defaultPagination: PaginatedParams<IProduct> = {
  page: 0,
  limit: 12,
  offset: 0,
  sort: [ [ "description", "ASC" ] ]
};

/**
 * Main "Products" View
 *
 * @returns
 */
const ProductsManagementPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const TableRef = React.useRef<ITableInstance<IProduct>>(null);
  const [ showImportModal, setShowImportModal ] = React.useState(false);
  const [ showNutritionImportModal, setShowNutritionImportModal ] = React.useState(false);
  const [ showExportModal, setShowExportModal ] = React.useState(false);
  const [ displayDelistedProducts, setDisplayDelistedProducts ] = React.useState(false);
  const [ displayUnpublishedProducts, setDisplayUnpublishedProducts ] = React.useState(false);
  const [ exportNutritionProductsResponse, exportNutritionProducts ] = useNutritionApi("EXPORT_PRODUCT_NUTRITION");
  const [ syncProductResponse, syncProductRequest ] = useConnectorApi("IMPORT_API_PRODUCT");
  const [ triggerFtpImportResponse, triggerFtpImportRequest ] = useImportSettingApi("TRIGGER_FTP_IMPORT");
  const [ showERPImportModal, setShowERPImportModal ] = React.useState(false);
  const [ currentView, setCurrentView ] = React.useState<CurrentView>("table");

  const {
    currentRole, installedModules, importSettings, erp, orderVariants
  } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    erp: global.settings && global.settings.erp.name,
    installedModules: global.settings?.installedModules,
    importSettings: global.settings?.importSettings,
    orderVariants: global.settings?.orderVariants
  }));

  const { terminology } = props;
  const [ listProductsResponse, listProducts ] = useProductsApi("LIST_PRODUCTS");
  const productsData = listProductsResponse.data;
  const checkPermission = useCheckUserPermission();

  const canCreateProduct = React.useMemo(() => {
    return checkPermission("create-product");
  }, [ checkPermission ]);

  React.useEffect(() => {
    if (triggerFtpImportResponse.data) {
      toast.success(triggerFtpImportResponse.data.message);
    } else if (triggerFtpImportResponse.error) {
      toast.error(triggerFtpImportResponse.error.message);
    }
  }, [ triggerFtpImportResponse.data, triggerFtpImportResponse.error ]);

  React.useEffect(() => {
    if (exportNutritionProductsResponse.data) {
      toast.success(exportNutritionProductsResponse.data.message);
    } else if (exportNutritionProductsResponse.error) {
      toast.error(exportNutritionProductsResponse.error.message);
    }
  }, [ exportNutritionProductsResponse ]);

  const onFetchData = React.useCallback((params: {filters: ListProductsFilters, pagination: PaginatedParams<IProduct>}) => {
    listProducts({
      filters: {
        ...params.filters,
        includeDelisted: displayDelistedProducts,
        includeUnpublished: displayUnpublishedProducts
      },
      pagination: params.pagination
    });
  }, [
    displayDelistedProducts,
    displayUnpublishedProducts,
    listProducts
  ]);

  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ ]);

  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ displayDelistedProducts ]);

  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ displayUnpublishedProducts ]);

  /**
   * Returns the buttons to go in the like to do actions
   */
  const ProductButtons = React.useMemo(() => {
    const buttons = [
      <Button
        key="export-products"
        onClick={() => setShowExportModal(true)}
      >
        {`Export ${terminology.product.plural.text}`}
      </Button>

    ];

    const hasProductCSVImportSetting = _.find(importSettings, is => is.type === ImportSettingType.Product && is.origin === ImportOrigin.CSV);

    if (hasProductCSVImportSetting && canCreateProduct) {
      buttons.push((
        <Button
          key="create-product"
          onClick={() => props.history.push("/admin/products/create")}
        >
          {`Create new ${
            terminology.product.singular.text
          }`}
        </Button>
      ));

      buttons.push((
        <Button
          key="import-products"
          onClick={() => setShowImportModal(true)}
        >
          {`Import ${terminology.product.plural.text}`}
        </Button>
      ));
    }

    if (installedModules?.includes("nutrition")) {
      buttons.push((
        <Button
          key="import-nutrition"
          onClick={() => setShowNutritionImportModal(true)}
        >
          Import Nutrition
        </Button>
      ));

      buttons.push((
        <Button
          key="export-nutrition"
          onClick={() => exportNutritionProducts(null)}
          loading={exportNutritionProductsResponse.pending}
        >
          Export Nutrition
        </Button>
      ));
    }

    // currently only allow unity to import individual product
    if (erp === ERPUnity || erp === ERPOdoo) {
      buttons.unshift(
        <Button
          key="import-product"
          onClick={() => setShowERPImportModal(true)}
        >
          {`Import From ${capitalize(erp)}`}
        </Button>);
    }
    const hasProductSFTPImportSetting = _.find(importSettings, is => is.type === ImportSettingType.Product && is.origin === ImportOrigin.SFTP);

    if (hasProductSFTPImportSetting && canCreateProduct) {
      buttons.push((
        <Button
          key="create-product"
          onClick={() => triggerFtpImportRequest(hasProductSFTPImportSetting.id)}
        >
          {`Sync ${
            terminology.product.singular.text
          } from FTP`}
        </Button>
      ));
    }

    return buttons;
  }, [
    terminology.product.plural.text,
    terminology.product.singular.text,
    importSettings,
    canCreateProduct,
    installedModules,
    erp,
    props.history,
    exportNutritionProductsResponse.pending,
    exportNutritionProducts,
    triggerFtpImportRequest
  ]);

  const returnActions = React.useCallback((row: any) => {
    const canAmendProduct = checkPermission("amend-product");

    const actions = [
      {
        title: `View ${terminology.product.singular.text}`,
        action: () => props.history.push(`/products/${row.original.id}`)
      }
    ];

    if (canAmendProduct) {
      actions.push({
        title: `Edit ${terminology.product.singular.text}`,
        action: () => props.history.push(`/admin/products/${row.original.id}/details`)
      });
    }

    // currently only allow unity and odoo to import individual product
    if (erp === ERPUnity || erp === ERPOdoo) {
      actions.push({
        title: `Import from ${capitalize(erp)}`,
        action: () => {
          syncProductRequest({ partNumber: row.original.partNumber });
        }
      });
    }

    return actions;
  }, [
    checkPermission,
    erp,
    props.history,
    syncProductRequest,
    terminology.product.singular.text
  ]);

  /** Handle the api product import response by showing a toast */
  React.useEffect(() => {
    if (syncProductResponse.data) {
      toast.success("Product import has been added to the job queue and will be processed shortly");
    } else if (syncProductResponse.error) {
      toast.error(syncProductResponse.error.message);
    }
  }, [ syncProductResponse ]);

  const columns = React.useMemo(() => {
    return getProductTableColumns({
      actionButton: info => (
        <ActionButton
          loading={syncProductResponse.pending}
          title="actions"
          items={returnActions(info.row)}
        />
      ),
      terminology: props.terminology,
      currentRole,
      pricesVisible: false,
      possibleOrderVariants: orderVariants
    });
  }, [
    currentRole,
    orderVariants,
    props.terminology,
    returnActions,
    syncProductResponse.pending
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={(
          <LikeToDo
            copy="What would you like to do?"
            buttons={ProductButtons}
          />
        )}
      >
        <TableHeader>
          <div className="quick-filters">
            <Text weight="bold">
              Include delisted products:
            </Text>

            <CheckboxStyled
              checked={displayDelistedProducts}
              onChange={e => setDisplayDelistedProducts(e.target.checked)}
            />

            <Text
              weight="bold"
              style={{ marginLeft: "30px" }}
            >
              Include unpublished products:
            </Text>

            <CheckboxStyled
              checked={displayUnpublishedProducts}
              onChange={e => setDisplayUnpublishedProducts(e.target.checked)}
            />
          </div>

          <div className="view-options">
            <GridViewButton
              toggleCallBack={setCurrentView}
              view={currentView}
            />
          </div>

        </TableHeader>

        {currentView === "table" && (
          <Table
            ref={TableRef}
            loading={listProductsResponse.pending}
            tableOf="Products"
            defaultPagination={defaultPagination}
            data={listProductsResponse.data ? listProductsResponse.data.result : []}
            onFetchData={onFetchData}
            pageCount={listProductsResponse.data ? listProductsResponse.data.pages : 0}
            rowCount={listProductsResponse.data ? listProductsResponse.data.count : 0}
            columns={columns}
            error={listProductsResponse.error}
          />
        )}

        {currentView === "grid" && (

          <ProductsWrapper>

            <Grid.Bounds className="product-list">

              <Grid.Box width="100%">

                <div className="products-list grid">
                  {_.map(productsData?.result, prod => (
                    <div
                      className="product-card-wrapper"
                      key={`product-${prod.id}`}
                    >
                      <ProductCard
                        productImage={prod.image || ""}
                        product={prod}
                        drop={null}
                        view="grid"
                      />
                    </div>
                  ))}
                </div>

                {listProductsResponse.pending && (
                  <LoadingSpinner
                    type="flat"
                    position="inline"
                  />
                )}

              </Grid.Box>
            </Grid.Bounds>

          </ProductsWrapper>

        )}

        <ImportModal
          open={showImportModal}
          onDismiss={() => setShowImportModal(false)}
          type="product"
          terminology={props.terminology}
        />

        <ImportModal
          open={showNutritionImportModal}
          onDismiss={() => setShowNutritionImportModal(false)}
          type="nutrition"
          terminology={props.terminology}
        />

        <ProductExportModal
          open={showExportModal}
          onDismiss={() => setShowExportModal(false)}
        />

        <ConnectorProductImportModal
          open={showERPImportModal}
          onDismiss={() => setShowERPImportModal(false)}
        />
      </MainContentWrapper>
    </Layout>
  );
};

const TableHeader = styled.div`
${({ theme }) => css`
  display: flex;
  justify-content: space-between;
  background-color: ${theme.colors.white};

  .quick-filters {
      display: flex;
      align-items: center;
      margin: ${theme.spacing.margins.md};


      input {
        margin-left: ${theme.spacing.margins.md};

        &:hover {
          cursor: pointer;
        }
      }
  };
  .view-options {
    margin: ${theme.spacing.margins.md};
    display: flex;
    align-items:center;

  };
`}`;

const ProductsWrapper = styled.div`
${({ theme }) => css`

    .products-list {
      margin-top: ${theme.spacing.margins.md};
    }

    .products-list.grid {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between !important;
      width: 100%;

      .product-card-wrapper {
        background: ${theme.colors.white};
        box-shadow: ${theme.shadows.default};
        width: 100%;
        max-width: 400px;
        margin-bottom: ${theme.spacing.margins.md};

        > div {
          margin: auto;
        }

        .pack-size-selector-wrapper, .order-control-wrapper, .quantity-controls {
          display: flex;
          justify-content: flex-end;
        }

        .pack-quantity-label span {
          text-align: end;
        }
      }

      ${breakpoint("lg", theme)} {
        justify-content: flex-start;

        .product-card-wrapper {
          width: 48%;
          
          &:nth-child(odd) {
            margin-right: 2%;
          }
        }
      }

      ${breakpoint("xl", theme)} {
        .product-card-wrapper {
          width: 31%;
          margin-right: calc(4.666% / 2);

          &:nth-child(odd) {
            margin-right: calc(4.666% / 2);
          }
          
          &:nth-child(3n) {
            margin-right: 0;
          }
        }
      }

      ${breakpoint("xxl", theme)} {
        .product-card-wrapper {
          width: 23%;
          margin-right: calc(6% / 3);

          &:nth-child(odd) {
            margin-right: calc(6% / 3);
          }
          
          &:nth-child(3n) {
            margin-right: calc(6% / 3);
          }
        }
      }
    }
    
    .products-list.compact {
      .product-card-wrapper {
        margin-bottom: ${theme.spacing.margins.md};
      }
    }

    .toggle-product-view-wrapper {
      margin-top: ${theme.spacing.margins.sm};
      margin-right: ${theme.spacing.margins.md};
      display: flex;
      justify-content: flex-end;

      .toggle-product-view {
        display: flex;
        align-items: center;

        svg {
          margin-left: ${theme.spacing.margins.sm};
        }

        &:hover {
          cursor: pointer;
        }
      }
    }

`}  
`;

export default ProductsManagementPage;
